import { computed, type ComputedRef } from "vue"
import { useTheme } from "vuetify"
import { useLayoutStore } from "@/store/layout"

/**
 * Light/dark appearance, as the user expresses it.
 *
 * Note this is the *preference*, not the resulting theme: "system" is a standing instruction to
 * follow `prefers-color-scheme`, and the theme it resolves to changes under us when the OS
 * switches at sunset. Vuetify models the same distinction (`theme.isSystem` vs `theme.name`), and
 * everything here is a thin, typed wrapper over it.
 */
export type ThemePreference = "system" | "light" | "dark"

/** Offered in this order in the picker: the automatic option first, then the two manual ones. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"]

/**
 * The app was light-only until the dark theme was finished, so an existing user who has never
 * opened the picker keeps exactly the app they had. "system" is a deliberate opt-in rather than
 * the default: inheriting a dark OS on upgrade would be a surprise, not a feature.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "light"

/** Sits next to `locale` and `navbar`; written by `useLayoutStore().setTheme`. */
export const THEME_STORAGE_KEY = "theme"

function isThemePreference(value: unknown): value is ThemePreference {
  return THEME_PREFERENCES.includes(value as ThemePreference)
}

/**
 * The persisted preference, validated.
 *
 * Called at module scope by `plugins/vuetify.js` to seed `defaultTheme`, which is why it reads
 * localStorage directly instead of going through the (not yet installed) pinia store. The
 * validation is not defensive noise: Vuetify looks `defaultTheme` up in its theme map with no
 * fallback, so a stale or hand-edited key would leave `theme.current` undefined and take the
 * stylesheet — every `--v-theme-*` variable in the app — down with it.
 */
export function readThemePreference(): ThemePreference {
  const stored = typeof localStorage === "undefined" ? null : localStorage.getItem(THEME_STORAGE_KEY)
  return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE
}

export interface UseThemePreference {
  /** What the user chose — "system" stays "system", it does not collapse to the resolved theme. */
  preference: ComputedRef<ThemePreference>
  /** What that currently resolves to. Use this for "does the UI look dark right now" decisions. */
  isDark: ComputedRef<boolean>
  setPreference: (_value: ThemePreference) => void
}

/**
 * Read and change the appearance preference from a component.
 *
 * `theme.change()` is applied to Vuetify's global theme instance, so a change made in the toolbar
 * repaints the whole app at once — there is no per-component wiring to remember.
 */
export function useThemePreference(): UseThemePreference {
  const theme = useTheme()
  const layout = useLayoutStore()

  const preference = computed<ThemePreference>(() =>
    theme.isSystem.value ? "system" : (theme.name.value as ThemePreference)
  )

  const isDark = computed(() => theme.current.value.dark)

  function setPreference(value: ThemePreference): void {
    if (!isThemePreference(value)) return
    theme.change(value)
    layout.setTheme(value)
  }

  return { preference, isDark, setPreference }
}
